import React, { createContext, useContext, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { colors } from '../../../core';
import Icon from '../Icon';
import Label from '../Label';
import Tag from '../Tag';

export interface TagInputProps {
  name: string;
  label: string;
  disabled?: boolean;
}

export interface TagContextProps {
  selectedTags: TagInputProps[];
  handleSelectChange?: (tag: TagInputProps) => void;
}

export interface TagGroupProps {
  children?: React.ReactNode;
  initialSelected: TagInputProps[];
  onChange: (value: TagInputProps[]) => void;
  label?: string;
}

export interface StyledTagProps {
  color: string;
  onClick?: () => void;
}

const Checkbox = styled.div<{ checked: boolean }>`
  background-color: ${colors.white};
  border-radius: 3px;
  border: 1px solid ${({ theme }) => theme.fontColor || colors.ash};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-right: 4px;
  overflow: hidden;

  ${({ checked }) =>
    checked &&
    css`
      background-color: ${colors.success};
      border-color: transparent;
    `}
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const StyledTag = styled(Tag).attrs(({ theme }) => ({
  color: theme.fontColor || colors.ash,
}))<{ disabled?: boolean; isSelected?: boolean }>`
  align-items: center;
  display: inline-flex;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  padding: 4px;
  opacity: ${({ disabled }) => (disabled ? '0.3' : '1')};
  border-color: ${({ isSelected }) => (isSelected ? colors.success : 'currentColor')};
`;

const TagContext = createContext<TagContextProps>({
  selectedTags: [{ name: '', label: '' }],
});

const TagInput = ({ name, label, disabled }: TagInputProps) => {
  const { selectedTags, handleSelectChange }: TagContextProps = useContext(TagContext);
  const isSelected = selectedTags.some((tag) => tag.name === name);

  const handleTagClick = () => {
    if (!disabled) {
      handleSelectChange && handleSelectChange({ name, label });
    }
  };

  return (
    <StyledTag onClick={handleTagClick} disabled={disabled} isSelected={isSelected}>
      <Checkbox checked={isSelected}>
        {isSelected && <Icon color={colors.white} size={12} viewSize={12} type="done" />}
      </Checkbox>
      {label}
    </StyledTag>
  );
};

const TagGroup = ({ children, initialSelected, label, onChange }: TagGroupProps) => {
  const [selectedTags, setSelectedTags] = useState<TagInputProps[] | []>([]);

  useEffect(() => {
    if (initialSelected.length) {
      setSelectedTags(initialSelected);
    }
  }, [initialSelected]);

  const handleSelectChange = (tag: TagInputProps) => {
    const isTagSelected = selectedTags.some(
      (selectedTag: TagInputProps) => selectedTag.name === tag.name
    );

    const newSelection: TagInputProps[] | [] = isTagSelected
      ? selectedTags!.filter((item: TagInputProps) => item.name !== tag.name)
      : [...selectedTags, tag];
    setSelectedTags(newSelection);
    onChange(newSelection);
  };

  return (
    <TagContext.Provider value={{ selectedTags, handleSelectChange }}>
      {label && <Label>{label}</Label>}
      <Row>{children}</Row>
    </TagContext.Provider>
  );
};

TagGroup.Input = TagInput;

export default TagGroup;
